import { useMemo } from 'react';
import useCaptainData from 'src/common/hooks/useCaptainData';
import type { ImmutableTrip } from 'src/types/trips/Trips';
import { useSelector } from 'react-redux';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import { getOffer } from 'src/common/utils/reduxStoreSelectors';

type Errors = {
  captain?: boolean;
  pickupTime?: boolean;
  returnTime?: boolean;
  duration?: boolean;
};

/**
 * The Offer pages compose multiple Forms, where each form is returned within a modal.
 * Each implements Form['validate']- but local form validation only runs when the form is mounted.
 * So it is possible that submitting the date form could invalidate time values,
 * but the user would not know until opening the time form modal again.
 *
 * `UseOfferErrors` acts like a multi-form validator.
 * Errors are returned to display on each card,
 * or above the whole offer submit CTAs (via OfferEdit component).
 */
const useOfferErrors = (trip: ImmutableTrip, isEditOffer?: boolean) => {
  const offer = useSelector(getOffer);
  const { captainOptionHeading, showCaptainConflict } = useCaptainData(trip, offer, isEditOffer);
  const captainError = !captainOptionHeading.key || showCaptainConflict;
  const { offerTrip: { hasPickupTime, durationMoment } } = useSelector(dateTimeDurationSelector(trip.get('pk')));

  const errors = useMemo(() => {
    const error: Errors = {};
    if (captainError) {
      error.captain = true;
    }
    if (!hasPickupTime) {
      // Possible on inquiries submitted before Sep 2025, when depart time was not required.
      error.pickupTime = true;
    }
    if (durationMoment.asMinutes() <= 0) {
      error.duration = true;
      // dates form does now allow endDate before startDate, so the only way this occurs
      // is if returnTime is equal to startTime, thus show the error on returnTime
      error.returnTime = true;
    }
    return error;
  }, [captainError, durationMoment, hasPickupTime]);

  const hasErrors = Object.keys(errors).length > 0;

  return useMemo(() => ({
    errors,
    hasErrors,
  }), [
    errors,
    hasErrors,
  ]);
};

export default useOfferErrors;
